import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Disc3 } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { mutateListOnPut } from '@/helpers/swr.js';

import { Form } from '@/components/ui/form';
import Error from '@/components/common/Error';
import { Button } from '@/components/ui/button';

import { activityFormSchema } from './_validators';
import FormGeneral from './components/common/FormGeneral';

import { usePutActivity, useActivitiesV2, useActivity } from '@/data/activity';

const EditActivityForm = () => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const putActivity = usePutActivity();
  const activities = useActivitiesV2();
  const { id } = useParams();
  const activity = useActivity(id);
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(activityFormSchema),
    defaultValues: {
      title: activity.data.title,
      icon: activity.data.icon,
      feePercentage: activity.data.feePercentage
        ? activity.data.feePercentage / 100
        : 0,
      isWaiverRequired: activity.data.isWaiverRequired,
      isFirstAidRequired: activity.data.isFirstAidRequired,
      isQualificationRequired: activity.data.isQualificationRequired,
      isLocationRequired: activity.data.isLocationRequired,
      servicesContractStr:
        activity.data.servicesContractStr ||
        'coaching / instructing and/or guiding',
      activityQualifications: activity.data.activityQualifications.map(
        (qual) => ({
          name: qual.name,
          description: qual.description
        })
      ),
      isEnabled: activity.data.isEnabled
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const payload = {
        ...values,
        feePercentage: values.feePercentage * 100,
        activityQualifications: values.activityQualifications.map(
          (qual, index) => ({
            ...qual,
            sortOrder: index + 1
          })
        )
      };

      const updatedActivity = await putActivity(id, payload);
      await activities.mutate(
        (current) => mutateListOnPut(current, updatedActivity),
        false
      );
      await activity.mutate(updatedActivity, false);
      navigate(`/activity`);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormGeneral />
        {apiError && <Error error={apiError} />}
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting}>
            {submitting && <Disc3 className="animate-spin" />}
            Save Activity
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default EditActivityForm;
